import React, { useState, useEffect, useMemo } from "react";
import GlobalSearchModal from "./GlobalSearchModal";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Mail,
  UploadCloud,
  Send,
  FileText,
  Clock,
  AlertCircle,
  Settings,
  LayoutDashboard,
  Building2,
  Search,
  CheckCircle2,
  ShieldCheck,
  Key,
  ShieldBan,
  Activity,
  MonitorSmartphone,
  Users,
  Workflow,
  DollarSign,
  ClipboardList,
  BarChart3,
  CreditCard,
  FileSignature,
  X,
  Inbox,
  ChevronDown,
  PanelLeftClose,
  PanelLeftOpen,
  Layers,
  Signal,
  Globe,
  Network,
  Plus,
} from "lucide-react";
import { cn } from "../lib/utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
  SheetClose,
} from "./ui/sheet";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import {
  setSidebarOpen,
  toggleIsCollapsed,
  setSearchOpen,
} from "../store/slices/uiSlice";
import {
  setActiveWorkspace,
  createWorkspace,
} from "../store/slices/workspaceSlice";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useEntitlements } from "../contexts/EntitlementsContext";
import { useEntitlementBlockerModal } from "../contexts/EntitlementBlockerModalContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export default function Sidebar() {
  const dispatch = useDispatch();
  const { sidebarOpen, isCollapsed, searchOpen } = useSelector(
    (state) => state.ui,
  );
  const { workspaces, activeWorkspace, loading } = useSelector(
    (state) => state.workspace,
  );
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [docOpen, setDocOpen] = useState(false);
  const [createWorkspaceOpen, setCreateWorkspaceOpen] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const {
    hasEntitlement,
    getLimit,
    isLimitReached,
    snapshot,
    isLoading: entitlementsLoading,
  } = useEntitlements();
  const { openBlockerModal } = useEntitlementBlockerModal();

  const maxWorkspacesBucket = getLimit("maxWorkspaces");
  const entitlementReady = !!snapshot && !entitlementsLoading;
  const canCreateWorkspaceFeature = !entitlementReady
    ? true
    : hasEntitlement("canCreateWorkspace");
  const isWorkspaceLimitReached = !entitlementReady
    ? false
    : isLimitReached("maxWorkspaces");
  const canCreateWorkspace =
    canCreateWorkspaceFeature && !isWorkspaceLimitReached;
  const workspaceLimitSummary = useMemo(() => {
    const used = Number(maxWorkspacesBucket?.used || 0);
    const limitRaw = maxWorkspacesBucket?.limit;
    const limit = Number.isFinite(Number(limitRaw)) ? Number(limitRaw) : null;
    if (limit === null) return `${used} / Unlimited`;
    return `${used} / ${limit}`;
  }, [maxWorkspacesBucket]);

  const openCreateWorkspaceModal = () => {
    if (canCreateWorkspace) {
      setCreateWorkspaceOpen(true);
      return;
    }

    if (!canCreateWorkspaceFeature) {
      openBlockerModal({
        blockerCategory: "FEATURE_LOCKED",
        details: {
          featureKey: "canCreateWorkspace",
          planCode: snapshot?.plan?.code,
        },
        message:
          "Your current plan does not allow creating additional workspaces.",
        context: { surface: "sidebar", action: "workspace-create" },
      });
      return;
    }

    openBlockerModal({
      blockerCategory: "LIMIT_REACHED",
      details: {
        limitKey: "maxWorkspaces",
        planCode: snapshot?.plan?.code,
        currentUsage: maxWorkspacesBucket?.used,
        allowedLimit: maxWorkspacesBucket?.limit,
      },
      message: `Workspace limit reached (${workspaceLimitSummary}). Upgrade to add more workspaces.`,
      context: { surface: "sidebar", action: "workspace-create" },
    });
  };

  const handleCreateWorkspace = async (e) => {
    e.preventDefault();
    if (!canCreateWorkspace) {
      openCreateWorkspaceModal();
      return;
    }
    if (!newWorkspaceName.trim()) return;

    setIsCreating(true);
    try {
      await dispatch(createWorkspace(newWorkspaceName.trim())).unwrap();
      setCreateWorkspaceOpen(false);
      setNewWorkspaceName("");
    } catch (error) {
      console.error("Failed to create workspace:", error);
    } finally {
      setIsCreating(false);
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        dispatch(setSearchOpen(true));
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [dispatch]);

  useEffect(() => {
    dispatch(setSidebarOpen(false));
  }, [location.pathname, dispatch]);

  const SIDEBAR_CONFIG = useMemo(
    () => [
      {
        group: t("sidebar.core") || "Core",
        items: [
          {
            href: "/dashboard",
            icon: LayoutDashboard,
            label: t("sidebar.dashboard"),
          },
          { href: "/discovery", icon: Globe, label: t("sidebar.discovery") },
          { href: "/inbox", icon: Inbox, label: t("sidebar.inboxReplies") },
          { href: "/tasks", icon: ClipboardList, label: t("sidebar.tasks") },
        ],
      },
      {
        group: t("sidebar.crm"),
        items: [
          { href: "/crm/contacts", icon: Users, label: t("sidebar.contacts") },
          { href: "/crm/companies", icon: Building2, label: t("sidebar.companies") },
          {
            href: "/crm/lists",
            icon: ClipboardList,
            label: t("sidebar.lists"),
            activeMatch: "/crm/lists",
          },
          { href: "/groups", icon: Users, label: t("sidebar.groups") },
          { href: "/opportunities", icon: DollarSign, label: t("sidebar.opportunities") },
        ],
      },
      {
        group: t("sidebar.outreach"),
        items: [
          {
            href: "/sequences",
            icon: Workflow,
            label: t("sidebar.sequences"),
            entitlementKey: "canUseSequences",
            showWhenLocked: true,
          },
          { href: "/campaigns", icon: Send, label: t("sidebar.campaigns") },
          {
            href: "/templates",
            icon: FileSignature,
            label: t("sidebar.templates"),
            activeMatch: "/templates",
          },
          { href: "/queue", icon: Mail, label: t("sidebar.allEmails") },
          { href: "/processing", icon: Clock, label: t("sidebar.processing") },
          {
            href: "/not-sended",
            icon: AlertCircle,
            label: t("sidebar.queued"),
          },
          {
            href: "/batch",
            icon: Send,
            label: t("sidebar.batchSend"),
          },
          { href: "/suppressions", icon: ShieldBan, label: t("sidebar.blocklist") },
          { href: "/upload", icon: UploadCloud, label: t("sidebar.csvUpload") },
        ],
      },
      {
        group: t("sidebar.delivery"),
        items: [
          { href: "/mail-accounts", icon: Mail, label: t("sidebar.senderAccounts") },
          { href: "/sender-pools", icon: Layers, label: t("sidebar.senderPools") },
          { href: "/email-finder", icon: Search, label: t("sidebar.emailFinder") },
          { href: "/verified-emails", icon: CheckCircle2, label: t("sidebar.verified") },
          { href: "/saved-emails", icon: FileText, label: t("sidebar.savedScans") },
        ],
      },
      {
        group: t("sidebar.automation"),
        items: [
          {
            href: "/workflows",
            icon: Network,
            label: t("sidebar.orchestrator"),
            activeMatch: "/workflows",
            entitlementKey: "canUseWorkflows",
          },
          {
            href: "/workflows/runs",
            icon: Activity,
            label: t("sidebar.executionConsole"),
            entitlementKey: "canUseWorkflows",
          },
          {
            href: "/inbox/automations",
            icon: Workflow,
            label: t("sidebar.inboxAutomations"),
          },
        ],
      },
      {
        group: t("sidebar.growthSuccess"),
        items: [
          {
            href: "/sales-dashboard",
            icon: BarChart3,
            label: t("sidebar.salesDashboard"),
            entitlementKey: "canUseSalesDashboard",
          },
          { href: "/success", icon: ShieldCheck, label: t("sidebar.customerSuccess") },
        ],
      },
      {
        group: t("sidebar.opsAdmin"),
        items: [
          { href: "/ops", icon: Activity, label: t("sidebar.opsDashboard") },
          {
            href: "/ops/signals",
            icon: Signal,
            label: t("sidebar.signalGovernance"),
            entitlementKey: "canUseAdvancedGovernance",
          },
          {
            href: "/approvals",
            icon: ShieldCheck,
            label: "Approvals",
            entitlementKey: "canUseAdvancedGovernance",
          },
          { href: "/plan", icon: CreditCard, label: t("sidebar.planBilling") },
          {
            href: "/sessions",
            icon: MonitorSmartphone,
            label: t("sidebar.activeSessions"),
          },
          {
            href: "/vault",
            icon: Key,
            label: t("sidebar.vaultWebhooks"),
            entitlementKey: "canUseWebhooks",
          },
          {
            href: "/reports",
            icon: FileText,
            label: t("sidebar.complianceReports"),
            entitlementKey: "canUseAdvancedReporting",
          },
        ],
      },
      {
        group: t("sidebar.support") || "Support",
        items: [
          {
            label: t("sidebar.about"),
            icon: FileText,
            onClick: () => setDocOpen(true),
          },
          {
            href: "/documentation",
            icon: FileText,
            label: t("sidebar.documentation"),
          },
          { href: "/settings", icon: Settings, label: t("sidebar.settings") },
        ],
      },
    ],
    [t],
  );

  const filteredSidebarConfig = useMemo(() => {
    return SIDEBAR_CONFIG.map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        if (!item.entitlementKey) return true;
        if (!entitlementReady) return true;
        return hasEntitlement(item.entitlementKey) || item.showWhenLocked;
      }),
    })).filter((section) => section.items.length > 0);
  }, [SIDEBAR_CONFIG, entitlementReady, hasEntitlement]);

  const NavItem = ({
    href,
    icon: Icon,
    label,
    active,
    onClick,
    entitlementKey,
  }) => {
    const isLocked =
      !!entitlementKey && entitlementReady && !hasEntitlement(entitlementKey);

    const handleClick = () => {
      if (onClick) {
        onClick();
        return;
      }

      if (isLocked) {
        console.log("islocked test deneme")
        openBlockerModal({
          blockerCategory: "FEATURE_LOCKED",
          details: {
            featureKey: entitlementKey,
            planCode: snapshot?.plan?.code,
          },
          message: "This page is not available on your current plan.",
          context: { surface: "sidebar", featureKey: entitlementKey },
        });
        return;
      }

      navigate(href);
    };

    return (
      <button
        onClick={handleClick}
        className={cn(
          "group relative w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-ring mb-0.5",
          active
            ? "bg-accent/80 text-primary shadow-sm ring-1 ring-border/50"
            : "text-muted-foreground hover:bg-accent/40 hover:text-accent-foreground",
          isLocked && !active && "opacity-70",
          isCollapsed && "justify-center px-0 mb-2",
        )}
        title={isCollapsed ? label : undefined}
      >
        <Icon
          className={cn(
            "h-4 w-4 shrink-0 transition-colors duration-200",
            active
              ? "text-primary scale-110"
              : "text-muted-foreground group-hover:text-accent-foreground",
          )}
        />
        {!isCollapsed && (
          <span className="truncate tracking-tight leading-none">{label}</span>
        )}
        {active && (
          <div
            className={cn(
              "absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full bg-primary shadow-[0_0_8px_rgba(var(--primary),0.5)]",
              isCollapsed && "left-0 top-1.5 bottom-1.5 w-[4px]",
            )}
          />
        )}
      </button>
    );
  };

  const NavGroup = ({ title, children }) => (
    <div className="mb-8 last:mb-20">
      {!isCollapsed && title && (
        <h4 className="px-4 text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/40 mb-3 ml-0.5">
          {title}
        </h4>
      )}
      {isCollapsed && title && <div className="h-px bg-border/40 mx-3 mb-6" />}
      <div className={cn("space-y-0.5", isCollapsed && "space-y-2")}>
        {children}
      </div>
    </div>
  );

  return (
    <>
      <GlobalSearchModal
        isOpen={searchOpen}
        onClose={() => dispatch(setSearchOpen(false))}
      />

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => dispatch(setSidebarOpen(false))}
        />
      )}

      <div
        className={cn(
          "sidebar-nav fixed inset-y-0 left-0 z-50 flex flex-col bg-background border-r border-border transition-all duration-300 ease-in-out shadow-2xl lg:shadow-none",
          isCollapsed ? "w-[68px]" : "w-[300px] xl:w-[320px]",
          "lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        )}
      >
        {/* Brand Header */}
        <div
          className={cn(
            "h-16 flex border-b border-border shrink-0 bg-muted/5 relative overflow-hidden",
            isCollapsed
              ? "flex-col items-center justify-center py-2 space-y-1"
              : "flex-row items-center px-6 gap-3 justify-between",
          )}
        >
          {!isCollapsed && (
            <div
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => navigate("/dashboard")}
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary shadow-lg shadow-primary/25 border border-primary/20">
                <Mail className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="font-bold text-lg tracking-tighter text-foreground bg-clip-text text-transparent bg-gradient-to-br from-foreground to-foreground/70">
                Mailwise
              </span>
            </div>
          )}
          {isCollapsed && (
            <div
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[8px] bg-primary shadow-lg shadow-primary/25 border border-primary/20 cursor-pointer"
              onClick={() => navigate("/dashboard")}
              title={t("nav.goToDashboard")}
            >
              <Mail className="h-3 w-3 text-primary-foreground" />
            </div>
          )}

          <div
            className={cn(
              "flex items-center",
              isCollapsed ? "justify-center" : "gap-0.5",
            )}
          >
            <button
              onClick={() => dispatch(toggleIsCollapsed())}
              className={cn(
                "hidden lg:flex rounded-xl text-muted-foreground hover:bg-accent hover:text-foreground transition-all active:scale-95",
                isCollapsed ? "p-1" : "p-2",
              )}
              title={isCollapsed ? t("sidebar.expandSidebar") : t("sidebar.collapseSidebar")}
            >
              {isCollapsed ? (
                <PanelLeftOpen className="h-4 w-4" />
              ) : (
                <PanelLeftClose className="h-4 w-4" />
              )}
            </button>
            {!isCollapsed && (
              <button
                onClick={() => dispatch(setSidebarOpen(false))}
                className="lg:hidden p-2 rounded-xl text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Workspace Switcher */}
        <div
          className={cn(
            "px-4 py-5 border-b border-border bg-muted/10",
            isCollapsed && "px-2.5",
          )}
        >
          {loading ? (
            <div
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-card border border-border/60 animate-pulse",
                isCollapsed && "justify-center px-0 h-10 w-10 mx-auto",
              )}
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted border border-border" />
              {!isCollapsed && (
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-muted rounded w-3/4" />
                  <div className="h-2 bg-muted rounded w-1/2" />
                </div>
              )}
            </div>
          ) : workspaces?.length > 0 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-card border border-border/60 hover:border-primary/30 hover:bg-accent/50 transition-all text-left group shadow-sm",
                    isCollapsed && "justify-center px-0 h-10 w-10 mx-auto",
                  )}
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/5 border border-primary/10 group-hover:bg-primary/10">
                    <Building2 className="h-4.5 w-4.5 text-primary/70 group-hover:text-primary transition-colors" />
                  </div>
                  {!isCollapsed && (
                    <>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold truncate text-foreground leading-none mb-1">
                          {activeWorkspace?.name || t("sidebar.selectWorkspace")}
                        </p>
                        <p className="text-[9px] text-muted-foreground uppercase tracking-[0.15em] font-black opacity-40">
                          {activeWorkspace?.role || t("common.guest")}
                        </p>
                      </div>
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground/30 group-hover:text-muted-foreground transition-colors" />
                    </>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align={isCollapsed ? "start" : "center"}
                side={isCollapsed ? "right" : "bottom"}
                className="w-64 bg-popover border border-border shadow-2xl rounded-2xl p-1.5 animate-in zoom-in-95 duration-200"
              >
                <DropdownMenuLabel className="text-[10px] text-muted-foreground px-3 py-2 uppercase tracking-[0.2em] font-black opacity-40">
                  {t("sidebar.yourWorkspaces")}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-border/50 mx-1" />
                <ScrollArea className="max-h-[300px]">
                  {workspaces.map((ws) => (
                    <DropdownMenuItem
                      key={ws._id}
                      onClick={() => dispatch(setActiveWorkspace(ws))}
                      className="cursor-pointer text-sm rounded-xl flex items-center gap-3 py-3 px-3 focus:bg-primary/5 mb-1 last:mb-0 transition-all"
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/40 border border-border">
                        <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="truncate font-bold text-[13px]">
                          {ws.name}
                        </p>
                        <p className="text-[10px] text-muted-foreground uppercase font-black opacity-60 tracking-wider transition-all">
                          {ws.role}
                        </p>
                      </div>
                      {activeWorkspace?._id === ws._id && (
                        <div className="h-2 w-2 rounded-full bg-primary shadow-[0_0_8px_rgba(var(--primary),0.5)]" />
                      )}
                    </DropdownMenuItem>
                  ))}
                </ScrollArea>
                <DropdownMenuSeparator className="bg-border/50 mx-1" />
                <DropdownMenuItem
                  className={cn(
                    "text-sm rounded-xl flex items-center gap-3 py-3 px-3 mt-1 transition-all font-bold",
                    canCreateWorkspace
                      ? "cursor-pointer focus:bg-primary/5 text-primary"
                      : "opacity-50 cursor-not-allowed text-muted-foreground",
                  )}
                  onClick={openCreateWorkspaceModal}
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 border border-primary/20">
                    <Plus className="h-4 w-4" />
                  </div>
                  <span>{t("actions.createWorkspace")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-muted/5 border border-dashed border-border text-muted-foreground",
                isCollapsed && "justify-center px-0 h-10 w-10 mx-auto",
              )}
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                <Building2 className="h-4.5 w-4.5 opacity-20" />
              </div>
              {!isCollapsed && (
                <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">
                  {t("common.noWorkspaces")}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Quick Search Area */}
        {!isCollapsed && (
          <div className="px-4 py-4 shrink-0">
            <button
              onClick={() => dispatch(setSearchOpen(true))}
              className="w-full h-10 flex items-center gap-3 px-4 text-sm text-muted-foreground bg-muted/20 border border-border/50 rounded-xl hover:border-primary/30 hover:bg-background transition-all group focus:outline-none focus:ring-1 focus:ring-primary/20"
            >
              <Search className="h-4 w-4 shrink-0 group-hover:text-primary transition-colors" />
              <span className="flex-1 text-left text-[13px] font-medium opacity-60">
                {t("actions.searchCommands")}
              </span>
              <kbd className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold text-muted-foreground/40 bg-accent/30 px-2 py-0.5 rounded-lg border border-border/50">
                <span className="text-[10px]">⌘</span>K
              </kbd>
            </button>
          </div>
        )}
        {isCollapsed && (
          <div className="px-3 py-4 shrink-0 flex justify-center">
            <button
              onClick={() => dispatch(setSearchOpen(true))}
              className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-accent transition-all text-muted-foreground hover:text-primary border border-transparent hover:border-border group"
              title="Search (⌘K)"
            >
              <Search className="h-4.5 w-4.5 group-hover:scale-110 transition-transform" />
            </button>
          </div>
        )}

        {/* Navigation Scroll Area */}
        <div className="flex-1 overflow-y-auto px-4 py-2 scrollbar-thin scrollbar-thumb-muted hover:scrollbar-thumb-muted-foreground/20">
          {filteredSidebarConfig.map((section, idx) => (
            <NavGroup key={idx} title={section.group}>
              {section.items.map((item, idy) => (
                <NavItem
                  key={idy}
                  {...item}
                  active={
                    item.activeMatch
                      ? location.pathname.startsWith(item.activeMatch)
                      : location.pathname === item.href
                  }
                />
              ))}
            </NavGroup>
          ))}
        </div>
      </div>

      <Sheet open={docOpen} onOpenChange={setDocOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg p-0 overflow-hidden border-border bg-background shadow-2xl">
          <SheetHeader className="p-6 bg-muted/20 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                <FileText className="w-5 h-5" />
              </div>
              <div>
                <SheetTitle className="text-lg font-bold">
                  Mailwise Architecture
                </SheetTitle>
                <SheetDescription className="text-xs">
                  Project implementation and meta-data.
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>
          <div className="p-6 text-[13px] text-muted-foreground space-y-4 leading-relaxed">
            <p>
              The <strong>Mailwise Orchestrator</strong> is a comprehensive
              platform built for scale, featuring advanced sequence automation,
              CRM management, and deep delivery telemetry.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 rounded-xl bg-accent/30 border border-border/50">
                <p className="text-[10px] font-black uppercase tracking-widest opacity-40 mb-1">
                  Stack
                </p>
                <p className="font-bold text-foreground">React + Node.js</p>
              </div>
              <div className="p-3 rounded-xl bg-accent/30 border border-border/50">
                <p className="text-[10px] font-black uppercase tracking-widest opacity-40 mb-1">
                  Database
                </p>
                <p className="font-bold text-foreground">MongoDB</p>
              </div>
            </div>
            <p className="bg-primary/5 p-3 rounded-xl border border-primary/10 text-xs italic">
              Designed for high-performance outreach and enterprise-grade
              reliability.
            </p>
          </div>
          <SheetFooter className="p-4 bg-muted/20 border-t border-border">
            <SheetClose asChild>
              <Button
                variant="outline"
                className="h-9 px-6 rounded-xl text-xs font-bold uppercase tracking-widest"
              >
                Close
              </Button>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Sheet open={createWorkspaceOpen} onOpenChange={setCreateWorkspaceOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg p-0 overflow-hidden border-border bg-background shadow-2xl">
          <SheetHeader className="p-6 bg-muted/20 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                <Building2 className="w-5 h-5" />
              </div>
              <div>
                <SheetTitle className="text-lg font-bold">
                  New Workspace
                </SheetTitle>
                <SheetDescription className="text-xs">
                  Create a new collaborative environment.
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>
          <form onSubmit={handleCreateWorkspace}>
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <Label
                  htmlFor="name"
                  className="text-xs font-bold uppercase tracking-widest opacity-60"
                >
                  Workspace Name
                </Label>
                <Input
                  id="name"
                  placeholder="Enter workspace name..."
                  value={newWorkspaceName}
                  onChange={(e) => setNewWorkspaceName(e.target.value)}
                  className="h-11 rounded-xl bg-muted/30 border-border/50 focus:border-primary/30 focus:ring-primary/20 transition-all font-medium"
                  autoFocus
                />
              </div>
              <p className="text-[11px] text-muted-foreground leading-relaxed italic opacity-70">
                {t('sidebar.newWorkspaceHint', {
                  defaultValue: 'You will be assigned as the Owner of this new workspace. You can invite team members later in settings.',
                })}
              </p>
              {!canCreateWorkspace && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400 leading-relaxed font-medium">
                  {t('sidebar.workspaceBlocked', {
                    defaultValue: 'Workspace creation is blocked by your current entitlement ({{summary}}).',
                    summary: workspaceLimitSummary,
                  })}
                </p>
              )}
            </div>
            <SheetFooter className="p-4 bg-muted/20 border-t border-border gap-2">
              <SheetClose asChild>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-10 rounded-xl text-xs font-bold uppercase tracking-widest"
                >
                  {t('actions.cancel')}
                </Button>
              </SheetClose>
              <Button
                type="submit"
                disabled={
                  !newWorkspaceName.trim() || isCreating || !canCreateWorkspace
                }
                className="h-10 px-8 rounded-xl text-xs font-bold uppercase tracking-widest shadow-lg shadow-primary/20"
              >
                {isCreating ? t('actions.createWorkspaceSubmitting') : t('actions.createWorkspace')}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
    </>
  );
}
